import { useState } from 'react';
import { nextTierFor, tierForXp } from '@shared';
import { Avatar } from '../components/ui/Avatar';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { creditIconFor } from '../components/ui/currency';
import { Panel } from '../components/ui/Panel';
import { ProgressBar } from '../components/ui/ProgressBar';
import { StatTile } from '../components/ui/StatTile';
import { formatCreditAmount, formatCredits } from '../lib/format';
import { useGame } from '../game/store';
import { useSession } from '../state/session';
import layout from '../styles/layout.module.css';
import styles from './ProfilePage.module.css';

/**
 * The player's profile: who they are, where they sit on the status ladder, and
 * the club around them. Everything here is real server state — the ladder is
 * `progression.track` from `GET /api/game`, and current/next status derive from
 * it with the same shared helpers the server uses.
 */
export function ProfilePage() {
  const { user, theme, mode, profiles, activeProfileId, selectProfile } = useSession();
  const { balance, stats, progression, club, setAnonymous, naming } = useGame();
  const [savingAnonymity, setSavingAnonymity] = useState(false);

  if (!user) return null;

  const creator = profiles.find((profile) => profile.id === activeProfileId);
  const ladder = { tiers: progression.track };
  const tier = tierForXp(ladder, progression.xp);
  const nextTier = nextTierFor(ladder, progression.xp);

  async function toggleAnonymity() {
    if (savingAnonymity) return;
    setSavingAnonymity(true);
    await setAnonymous(!progression.anonymous);
    setSavingAnonymity(false);
  }

  /** What a tier hands over, as one short line under its name. */
  function tierRewards(entry: (typeof progression.track)[number]): string {
    const parts: string[] = [];
    if (entry.creditBonus > 0) parts.push(`+${formatCreditAmount(naming, entry.creditBonus)}`);
    if (entry.couponPercent > 0) parts.push(`${entry.couponPercent}% coupon`);
    parts.push(...entry.perks);
    return parts.join(' · ');
  }

  return (
    <>
      <section className={styles.identity}>
        <Avatar userId={user.id} avatarHash={user.avatar} name={user.displayName} size={92} ring />
        <div className={styles.identityBody}>
          <h2 className={styles.displayName}>{user.displayName}</h2>
          <span className={styles.username}>@{user.username}</span>
          <div className={styles.levelRow}>
            <span className={styles.levelPill}>
              <Icon name="trophy" size={13} />
              {tier.name}
            </span>
            <Badge tone={mode === 'discord' ? 'success' : 'warning'} dot>
              {mode === 'discord' ? 'Connected to Discord' : 'Local preview'}
            </Badge>
          </div>
        </div>
      </section>

      <Panel
        title="Status"
        subtitle={
          nextTier ? `${tier.name} → ${nextTier.name}` : `${tier.name} — the top of the ladder`
        }
      >
        <ProgressBar
          value={progression.xp - tier.xp}
          max={nextTier ? nextTier.xp - tier.xp : 1}
          label={creator ? `Standing with ${creator.name}` : 'Standing'}
          valueLabel={
            nextTier
              ? `${formatCredits(progression.xp)} / ${formatCredits(nextTier.xp)} ${naming.xpName}`
              : `${formatCredits(progression.xp)} ${naming.xpName}`
          }
        />
      </Panel>

      <div className={layout.grid}>
        <StatTile
          icon={creditIconFor(theme)}
          accent="gold"
          label={naming.creditNamePlural}
          value={formatCredits(balance)}
        />
        <StatTile
          icon="bolt"
          accent="brand"
          label={naming.xpName}
          value={formatCredits(progression.xp)}
        />
        <StatTile
          icon="wheel"
          accent="cyan"
          label="Spins played"
          value={formatCredits(stats.spins)}
        />
        <StatTile
          icon="trophy"
          accent="mint"
          label="Wins"
          value={formatCredits(stats.wins)}
          hint="non-empty spins"
        />
      </div>

      <div className={layout.split}>
        <Panel title="Status track" subtitle="What each status unlocks">
          <div className={styles.track}>
            {progression.track.map((entry) => {
              const reached = progression.xp >= entry.xp;
              const current = entry.id === tier.id;
              return (
                <div
                  key={entry.id}
                  className={`${styles.tier} ${current ? styles.tierCurrent : ''} ${
                    reached ? '' : styles.tierLocked
                  }`}
                >
                  <span className={styles.tierIcon}>
                    <Icon name={reached ? 'trophy' : 'lock'} size={17} />
                  </span>
                  <span className={styles.tierBody}>
                    <span className={styles.tierLabel}>
                      {entry.name}
                      {current && <span className={styles.tierYou}>You are here</span>}
                    </span>
                    <span className={styles.tierText}>{tierRewards(entry)}</span>
                  </span>
                  <span className={styles.tierXp}>
                    {entry.xp > 0 ? `${formatCredits(entry.xp)} ${naming.xpName}` : 'Start'}
                  </span>
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel title="The Club" subtitle={creator ? `${creator.name}'s circle` : 'The circle'}>
          {club.length === 0 ? (
            <p className={styles.clubEmpty}>Nobody here yet.</p>
          ) : (
            <div className={styles.club}>
              {club.map((member, index) => (
                <div
                  key={`${member.name ?? 'anon'}-${index}`}
                  className={`${styles.member} ${member.you ? styles.memberYou : ''}`}
                >
                  <span className={styles.memberRank}>#{index + 1}</span>
                  <span className={member.name ? styles.memberName : styles.memberAnon}>
                    {member.name ?? 'Anonymous member'}
                    {member.you && <span className={styles.memberYouTag}>you</span>}
                  </span>
                  <Badge tone={member.tierId === tier.id && member.you ? 'brand' : 'neutral'}>
                    {member.tierName}
                  </Badge>
                </div>
              ))}
            </div>
          )}
          <div className={styles.clubActions}>
            <Button
              variant="secondary"
              icon={progression.anonymous ? 'user' : 'lock'}
              disabled={savingAnonymity}
              onClick={() => void toggleAnonymity()}
            >
              {progression.anonymous ? 'Show my name' : 'Go anonymous'}
            </Button>
            {profiles.length > 1 && (
              <Button variant="secondary" icon="swap" onClick={() => selectProfile(null)}>
                Switch realm
              </Button>
            )}
          </div>
          <p className={styles.clubNote}>
            Going anonymous hides your name from other members. Your status and its perks stay.
          </p>
        </Panel>
      </div>
    </>
  );
}
